import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, X, Pencil } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export function ParticipantOverviewEditDrawer({ participantId, currentProfile, open, onClose, onSaved }) {
  const [form, setForm] = useState({
    goals_aspirations: [],
    allergies: [],
    family_support_network: [],
    cultural_linguistic_needs: { language: '', interpreter_required: false, cultural_preferences: [], notes: '' },
    hobbies_interests: [],
    primary_disability: '',
    disability_impact: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Load existing data from overview_profile or current participant fields
    const p = currentProfile || {};
    setForm({
      goals_aspirations: Array.isArray(p.goals_aspirations) ? [...p.goals_aspirations] : [],
      allergies: Array.isArray(p.allergies) ? [...p.allergies] : [],
      family_support_network: Array.isArray(p.family_support_network) ? p.family_support_network.map(f => ({ ...f })) : [],
      cultural_linguistic_needs: p.cultural_linguistic_needs ? { ...p.cultural_linguistic_needs } : { language: '', interpreter_required: false, cultural_preferences: [], notes: '' },
      hobbies_interests: Array.isArray(p.hobbies_interests) ? [...p.hobbies_interests] : [],
      primary_disability: p.primary_disability || '',
      disability_impact: Array.isArray(p.disability_impact) ? [...p.disability_impact] : [],
    });
  }, [open, currentProfile]);

  // List helpers
  const addItem = (key) => setForm(f => ({ ...f, [key]: [...f[key], ''] }));
  const updateItem = (key, i, val) => setForm(f => ({ ...f, [key]: f[key].map((v, idx) => idx === i ? val : v) }));
  const removeItem = (key, i) => setForm(f => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  const addFamily = () => setForm(f => ({ ...f, family_support_network: [...f.family_support_network, { name: '', relationship: '', notes: '', phone: '' }] }));
  const updateFamily = (i, key, val) => setForm(f => ({ ...f, family_support_network: f.family_support_network.map((m, idx) => idx === i ? { ...m, [key]: val } : m) }));
  const removeFamily = (i) => setForm(f => ({ ...f, family_support_network: f.family_support_network.filter((_, idx) => idx !== i) }));

  const setCultural = (key, val) => setForm(f => ({ ...f, cultural_linguistic_needs: { ...f.cultural_linguistic_needs, [key]: val } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        goals_aspirations: form.goals_aspirations.filter(g => g.trim()),
        allergies: form.allergies.filter(a => a.trim()),
        hobbies_interests: form.hobbies_interests.filter(h => h.trim()),
        disability_impact: form.disability_impact.filter(d => d.trim()),
        family_support_network: form.family_support_network.filter(f => f.name.trim()),
      };
      await api.put(`/participants/${participantId}/overview-profile`, payload);
      toast.success('Overview profile updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="overview-edit-drawer">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-sm"><Pencil className="w-4 h-4" /> Edit Overview Profile</DialogTitle>
          <DialogDescription>Update participant information visible in the Overview tab.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-auto space-y-5 pr-1">
          {/* Disability */}
          <section>
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Disability Details</p>
            <div className="space-y-2">
              <div className="space-y-1">
                <Label className="text-xs">Primary Condition</Label>
                <Input value={form.primary_disability} onChange={e => setForm(f => ({ ...f, primary_disability: e.target.value }))} className="h-8 text-sm" data-testid="edit-overview-disability" placeholder="e.g., Autism, Cerebral Palsy" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Impact / Needs</Label>
                  <Button variant="ghost" size="sm" className="h-5 text-[10px]" onClick={() => addItem('disability_impact')}><Plus className="w-3 h-3 mr-0.5" />Add</Button>
                </div>
                {form.disability_impact.map((d, i) => (
                  <div key={i} className="flex gap-1 mb-1">
                    <Input value={d} onChange={e => updateItem('disability_impact', i, e.target.value)} className="h-7 text-xs" placeholder="Impact..." />
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeItem('disability_impact', i)}><X className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Goals */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Goals & Aspirations</p>
              <Button variant="ghost" size="sm" className="h-5 text-[10px]" onClick={() => addItem('goals_aspirations')} data-testid="add-goal-btn"><Plus className="w-3 h-3 mr-0.5" />Add</Button>
            </div>
            {form.goals_aspirations.length === 0 && <p className="text-xs text-muted-foreground italic">No goals added</p>}
            {form.goals_aspirations.map((g, i) => (
              <div key={i} className="flex gap-1 mb-1" data-testid={`goal-row-${i}`}>
                <Input value={g} onChange={e => updateItem('goals_aspirations', i, e.target.value)} className="h-7 text-xs" placeholder="Goal..." />
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeItem('goals_aspirations', i)}><X className="w-3 h-3" /></Button>
              </div>
            ))}
          </section>

          {/* Allergies */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Allergies</p>
              <Button variant="ghost" size="sm" className="h-5 text-[10px]" onClick={() => addItem('allergies')} data-testid="add-allergy-btn"><Plus className="w-3 h-3 mr-0.5" />Add</Button>
            </div>
            {form.allergies.length === 0 && <p className="text-xs text-muted-foreground italic">No allergies recorded</p>}
            {form.allergies.map((a, i) => (
              <div key={i} className="flex gap-1 mb-1" data-testid={`allergy-row-${i}`}>
                <Input value={a} onChange={e => updateItem('allergies', i, e.target.value)} className="h-7 text-xs" placeholder="Allergy..." />
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeItem('allergies', i)}><X className="w-3 h-3" /></Button>
              </div>
            ))}
          </section>

          {/* Family */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Family & Support Network</p>
              <Button variant="ghost" size="sm" className="h-5 text-[10px]" onClick={addFamily} data-testid="add-family-btn"><Plus className="w-3 h-3 mr-0.5" />Add</Button>
            </div>
            {form.family_support_network.length === 0 && <p className="text-xs text-muted-foreground italic">No family members added</p>}
            {form.family_support_network.map((f, i) => (
              <div key={i} className="p-2 border rounded mb-2 space-y-1" data-testid={`family-row-${i}`}>
                <div className="flex gap-2">
                  <Input value={f.name} onChange={e => updateFamily(i, 'name', e.target.value)} className="h-7 text-xs flex-1" placeholder="Name" />
                  <Input value={f.relationship} onChange={e => updateFamily(i, 'relationship', e.target.value)} className="h-7 text-xs w-[100px]" placeholder="Relationship" />
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeFamily(i)}><X className="w-3 h-3" /></Button>
                </div>
                <div className="flex gap-2">
                  <Input value={f.phone} onChange={e => updateFamily(i, 'phone', e.target.value)} className="h-6 text-[10px] flex-1" placeholder="Phone" />
                  <Input value={f.notes} onChange={e => updateFamily(i, 'notes', e.target.value)} className="h-6 text-[10px] flex-1" placeholder="Notes" />
                </div>
              </div>
            ))}
          </section>

          {/* Cultural */}
          <section>
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Cultural & Linguistic Needs</p>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Language</Label>
                  <Input value={form.cultural_linguistic_needs.language} onChange={e => setCultural('language', e.target.value)} className="h-8 text-sm" data-testid="edit-overview-language" placeholder="e.g., English" />
                </div>
                <div className="flex items-end gap-2 pb-1">
                  <Checkbox id="interpreter" checked={form.cultural_linguistic_needs.interpreter_required} onCheckedChange={v => setCultural('interpreter_required', v)} />
                  <Label htmlFor="interpreter" className="text-xs cursor-pointer">Interpreter Required</Label>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Cultural Notes</Label>
                <Input value={form.cultural_linguistic_needs.notes} onChange={e => setCultural('notes', e.target.value)} className="h-8 text-sm" placeholder="Cultural preferences..." />
              </div>
            </div>
          </section>

          {/* Hobbies */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Hobbies & Interests</p>
              <Button variant="ghost" size="sm" className="h-5 text-[10px]" onClick={() => addItem('hobbies_interests')} data-testid="add-hobby-btn"><Plus className="w-3 h-3 mr-0.5" />Add</Button>
            </div>
            {form.hobbies_interests.length === 0 && <p className="text-xs text-muted-foreground italic">No hobbies added</p>}
            {form.hobbies_interests.map((h, i) => (
              <div key={i} className="flex gap-1 mb-1">
                <Input value={h} onChange={e => updateItem('hobbies_interests', i, e.target.value)} className="h-7 text-xs" placeholder="Hobby..." />
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeItem('hobbies_interests', i)}><X className="w-3 h-3" /></Button>
              </div>
            ))}
          </section>
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 border-t">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="overview-save-btn">{saving ? 'Saving...' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
